/**
 * The browser producer for `@esa/shared`'s telemetry contract.
 *
 * Read `packages/shared/src/telemetry.ts` first — its doc comment is the
 * rulebook this file has to honour: nothing a requester typed, never in the
 * request path, invisible to the person working. This file is the "never in
 * the request path" half made concrete: everything here buffers in memory
 * and leaves on its own schedule, off the thread of whatever the person is
 * actually doing.
 *
 * NO CONSENT MECHANISM, ON PURPOSE. This is a desk tool counting its own
 * usage and its own error rate, not a cross-site tracker — `doNotTrack` is
 * answered here only in this comment, so nobody adds a banner or an opt-out
 * later thinking one is missing: there is one, and it is "don't ship a
 * banner for an internal tool measuring itself".
 */
import {
  routeTemplate,
  scrubText,
  TELEMETRY_LIMITS,
  type TelemetryBatch,
  type TelemetryEvent,
} from '@esa/shared';

type Meta = Record<string, string | number | boolean | null>;

/**
 * `import.meta.env` is a Vite addition, not part of the `ImportMeta` the
 * base TypeScript lib knows about, and this repo's tsconfig does not carry
 * the `vite/client` types (adding them is outside this file's remit). Cast
 * once, here, rather than sprinkling `as unknown as` at every call site.
 */
const RELEASE: string =
  (import.meta as unknown as { env?: { VITE_RELEASE?: string } }).env?.VITE_RELEASE ?? 'dev';

const ENDPOINT = '/api/v1/telemetry';

/** Per producer's clock; the server stamps its own receipt time too. */
const nowIso = (): string => new Date().toISOString();

// ---------------------------------------------------------------------------
// Buffer
// ---------------------------------------------------------------------------

/**
 * How many events an open tab is allowed to hold before it starts dropping.
 *
 * The batch trigger below empties this at ~25 in normal operation, so 200 is
 * not the expected depth — it is the backstop for a burst (an error loop, a
 * render stuck re-throwing) that outruns the scheduled flush. Without a cap,
 * that is a tab left open all week with a buffer that only ever grows: a
 * memory leak with a stack trace as its symptom.
 */
const BUFFER_CAP = 200;

/** Flush once the buffer reaches roughly this many, rather than waiting for the timer. */
const BATCH_TRIGGER = 25;

/** The timer flush's period. */
const FLUSH_INTERVAL_MS = 15_000;

let buffer: TelemetryEvent[] = [];
let droppedCount = 0;

/**
 * `track` must never block whatever called it, so crossing `BATCH_TRIGGER`
 * schedules a flush rather than calling it inline — a microtask, not a
 * synchronous call, so a tight burst of `track()` calls (a loop that is
 * itself the bug being reported) finishes filling the buffer, cap included,
 * before anything tries to drain it. Guarded so a burst only ever queues one.
 */
let capFlushQueued = false;
function scheduleCapFlush(): void {
  if (capFlushQueued) return;
  capFlushQueued = true;
  queueMicrotask(() => {
    capFlushQueued = false;
    flush('cap');
  });
}

/**
 * The last handful of things this tab did, kept for a bug report.
 *
 * SEPARATE FROM THE BUFFER ABOVE, which is drained and emptied every fifteen
 * seconds — so by the time somebody notices a problem and reaches for the
 * button, the events that explain it have usually already gone. This keeps
 * a short tail that survives a flush.
 *
 * NAMES AND TIMINGS ONLY. No error messages, no stacks, no metadata: the
 * point is to reconstruct a sequence ("searched, opened a ticket, pressed
 * resolve, then it went wrong"), and the fuller versions of those events are
 * already in telemetry under the same user and the same minute. Anything
 * richer here would be a second, less careful copy of the same data with a
 * different place to leak from.
 */
const RECENT_CAP = 20;
let recent: { kind: string; name: string; at: string; status?: number }[] = [];

function rememberRecent(e: TelemetryEvent): void {
  recent.push({
    kind: e.kind,
    name: e.name,
    at: e.at ?? nowIso(),
    ...(typeof e.status === 'number' ? { status: e.status } : {}),
  });
  if (recent.length > RECENT_CAP) recent = recent.slice(-RECENT_CAP);
}

/** What this tab has been doing, oldest first. A copy; callers cannot edit it. */
export function recentEvents(): { kind: string; name: string; at: string; status?: number }[] {
  return recent.map((r) => ({ ...r }));
}

/**
 * Record one event. Never awaits, never throws, never retried if it fails
 * downstream — see `flush`. Wrapped in try/catch because the one failure
 * mode worse than losing an event is losing the click, save or render that
 * was reporting it: telemetry breaking the app it watches is the bug this
 * file exists to make impossible.
 */
export function track(event: TelemetryEvent): void {
  try {
    const entry: TelemetryEvent = { at: nowIso(), ...event };

    if (buffer.length >= BUFFER_CAP) {
      // Drop the OLDEST. The newest event is usually the one somebody would
      // ask about a minute from now; the oldest is the one furthest from
      // whatever is happening right now.
      buffer.shift();
      droppedCount++;
    }
    buffer.push(entry);
    rememberRecent(entry);

    if (buffer.length >= BATCH_TRIGGER) scheduleCapFlush();
  } catch {
    /* see the function comment: this must never propagate. */
  }
}

/**
 * Send whatever is buffered and clear it. Idempotent on an empty buffer —
 * every scheduled trigger (timer, cap, hidden) can race and call this more
 * than once, and a batch of zero events is not a thing the server should
 * ever have to reject.
 *
 * `trigger === 'hidden'` is the only case that prefers `sendBeacon`: a
 * fetch started as the tab is closing or backgrounded is exactly the one
 * that gets cancelled mid-flight, which loses precisely the events around
 * whatever just crashed or the last few minutes before the tab closed.
 */
/**
 * How to authenticate a flush.
 *
 * Injected rather than imported: `api.ts` imports `trackError` from here, and
 * importing `session` back from it would close a module cycle. `main.tsx`
 * owns both and calls this at boot.
 */
let bearer: (() => string | null) | null = null;

export function setTelemetryAuth(getToken: () => string | null): void {
  bearer = getToken;
}

export function flush(trigger: 'timer' | 'cap' | 'hidden' | 'manual' = 'manual'): void {
  if (buffer.length === 0) return;

  const events = buffer;
  buffer = [];

  if (droppedCount > 0) {
    // Reported as an event of its own rather than a batch-level field,
    // because `TelemetryBatch` has none — `meta` is where a producer puts a
    // small bounded fact, and "this client silently discarded N events" is
    // exactly that: a fact about the client, not about anything a person did.
    events.push({
      source: 'web',
      kind: 'action',
      name: 'telemetry.buffer_dropped',
      meta: { count: droppedCount },
      at: nowIso(),
    });
    droppedCount = 0;
  }

  send(events, trigger === 'hidden');
}

/**
 * Chunked to the server's own stated limit (`TELEMETRY_LIMITS.maxEventsPerBatch`)
 * rather than trusting the buffer cap to stay under it — the two numbers are
 * owned by different files for different reasons, and a batch over the
 * limit is rejected wholesale rather than partially recorded.
 */
function send(events: TelemetryEvent[], preferBeacon: boolean): void {
  for (let i = 0; i < events.length; i += TELEMETRY_LIMITS.maxEventsPerBatch) {
    const chunk = events.slice(i, i + TELEMETRY_LIMITS.maxEventsPerBatch);
    transmit({ release: RELEASE, events: chunk }, preferBeacon);
  }
}

/**
 * The transport. Never retried — a queue that retries a failed flush is the
 * thing this file exists to avoid (see the module doc comment): it grows
 * without bound exactly when the network is the problem, which is the worst
 * possible time for a client to start holding more state, not less.
 */
function transmit(batch: TelemetryBatch, outliveThePage: boolean): void {
  if (typeof fetch !== 'function') return;

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  // Absent before sign-in completes, and on the handful of events a boot
  // failure produces. Those are dropped by the server rather than sent
  // anonymously — an ingest endpoint that accepts unauthenticated posts is
  // an ingest endpoint anyone on the network can fill with junk.
  const token = bearer?.() ?? null;
  if (token) headers.Authorization = `Bearer ${token}`;

  try {
    void fetch(ENDPOINT, {
      method: 'POST',
      headers,
      body: JSON.stringify(batch),
      /**
       * NOT `sendBeacon`, deliberately. Beacon cannot carry the bearer, so
       * every beacon would arrive unauthenticated. `keepalive` gives the same
       * survives-the-tab-closing guarantee AND takes headers; its 64KB body
       * cap is far above a batch of a hundred small events.
       *
       * Set only when it is needed. A keepalive request competes for a small
       * per-origin budget, and spending it on the 15-second timer flush would
       * leave nothing for the one flush that actually races the tab closing.
       */
      keepalive: outliveThePage,
    }).catch(() => {
      /* NOT RETRIED — see the function comment above. */
    });
  } catch {
    /* fetch can throw synchronously (a malformed request); telemetry must
       not be able to take its caller down with it. */
  }
}

// ---------------------------------------------------------------------------
// Scheduling — the timer and the hidden-tab flush.
// ---------------------------------------------------------------------------

let flushTimer: ReturnType<typeof setInterval> | undefined;
let visibilityBound = false;

/**
 * Starts the periodic flush and the hidden-tab flush. Idempotent so calling
 * it more than once (main.tsx guards its own call site, but this is the
 * belt to that braces) never produces a second timer ticking alongside the
 * first — the same class of bug the cached React root exists to prevent,
 * just for an interval instead of a DOM node.
 */
export function initTelemetry(): void {
  if (flushTimer === undefined) {
    flushTimer = setInterval(() => flush('timer'), FLUSH_INTERVAL_MS);
  }

  if (!visibilityBound && typeof document !== 'undefined') {
    visibilityBound = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush('hidden');
    });
  }
}

// ---------------------------------------------------------------------------
// Producers
// ---------------------------------------------------------------------------

/**
 * `routeTemplate` (from `@esa/shared`) only knows to templatise ids and runs
 * of digits — it has no idea this app's OWN paths carry a tenant as their
 * first path segment, so `/t/esatech/120198` came back as
 * `/t/esatech/:n`: one row per group, which is exactly the "counted once
 * per ticket" problem `routeTemplate` exists to fix, just one segment
 * later. Every path this client ever calls or navigates to is one of these
 * two shapes, so both are generalised before handing off.
 */
export function templatePath(path: string): string {
  const withoutQuery = path.split('?')[0]!;
  const withTenant = withoutQuery
    .replace(/^\/api\/v1\/tenants\/[^/]+(?=\/|$)/, '/api/v1/tenants/:tenant')
    .replace(/^\/t\/[^/]+(?=\/|$)/, '/t/:tenant');
  return routeTemplate(withTenant);
}

/** A screen was opened. `path` is the raw pathname; this templates it before it is ever buffered. */
export function pageView(path: string): void {
  track({ source: 'web', kind: 'page', name: templatePath(path) });
}

/** A named thing a person chose to do — resolving, linking, parking. */
export function trackAction(name: string, meta?: Meta): void {
  track({
    source: 'web',
    kind: 'action',
    name: name.slice(0, TELEMETRY_LIMITS.maxNameLength),
    meta: boundMeta(meta),
  });
}

/**
 * Something threw. `context` is a stable identifier for WHERE — `window`,
 * `promise`, `boundary`, `api.network`, `api.status` — not the message,
 * because the message is the varying part and the name has to be countable.
 *
 * Deliberately does NOT compute `errorFingerprint`. The server groups
 * errors from the message and stack it receives (see `fingerprint` in
 * `@esa/shared`), and a client-computed grouping key the server merely
 * trusted would let a producer decide its own grouping — the whole point of
 * fingerprinting server-side is that no single client gets to.
 */
export function trackError(err: unknown, context: string, meta?: Meta): void {
  try {
    const { message, stack } = describeError(err);
    track({
      source: 'web',
      kind: 'error',
      name: `error.${context}`.slice(0, TELEMETRY_LIMITS.maxNameLength),
      errorMessage: scrubText(message),
      errorStack: trimStack(stack),
      meta: boundMeta(meta),
    });
  } catch {
    /* a broken error reporter must not itself become a second error. */
  }
}

function describeError(err: unknown): { message: string; stack: string | null } {
  if (err instanceof Error) return { message: err.message || err.name, stack: err.stack ?? null };
  if (typeof err === 'string') return { message: err, stack: null };
  try {
    return { message: JSON.stringify(err) ?? String(err), stack: null };
  } catch {
    return { message: 'unknown error', stack: null };
  }
}

/**
 * First ~8 frames, scrubbed. A full stack from a minified bundle is mostly
 * framework noise anyway, and `scrubText` still runs over what is kept —
 * a frame naming a local file path is as much a leak as a message quoting one.
 */
function trimStack(stack: string | null): string | null {
  if (!stack) return null;
  const frames = stack.split('\n').slice(0, 8).join('\n');
  return scrubText(frames, TELEMETRY_LIMITS.maxStackLength);
}

/**
 * Enforced client-side too, not only trusted to the server: `meta` is meant
 * to stay small and bounded (see the shared doc comment), and a caller that
 * hands this a dozen fields or one enormous string should have it silently
 * capped rather than have the whole event rejected downstream.
 */
function boundMeta(meta?: Meta): Meta | undefined {
  if (!meta) return undefined;
  const out: Meta = {};
  let n = 0;
  for (const [key, value] of Object.entries(meta)) {
    if (n >= TELEMETRY_LIMITS.maxMetaKeys) break;
    out[key] = typeof value === 'string' ? value.slice(0, TELEMETRY_LIMITS.maxMetaValueLength) : value;
    n++;
  }
  return out;
}
